import { LoaderService } from 'services/LoaderService';
import { Storage } from 'services/storage';

import { axios } from 'util/Api';
import { Notify } from 'util/notify';
import { catchError, getUserName, logWarn, objectToQueryString } from 'util/utils';

import { LOCAL_STORAGE_VAR, FILE_TYPES_PDF, FILE_TYPES } from 'constants/Common';
import { commonLogout } from 'constants/Common';
import { CREATE_ANALYTICS, NEXT_BID, GET_TIME } from 'constants/apiConstant';

const myUploadProgress = (progressEvent) => {
  const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);

  const showProgressMain = document.querySelector('#loader.APILoader .showProgress');
  if (showProgressMain?.classList?.contains?.('display-none')) {
    void showProgressMain?.classList?.remove?.('display-none');
  }

  const showProgressDoc = document.querySelector('#loader.APILoader .showProgress span');
  if (showProgressDoc) {
    showProgressDoc.innerText = `${percentCompleted}%`;
    showProgressDoc.style.width = `${percentCompleted}%`;
  }
};

export const callApi = async (object, callback) => {
  const token = object.updateToken ?? object.auth ?? Storage.get(`token`);

  object = { ...object, config: { ...object.config } };
  object.data = object.data ?? object.request;
  object.notify = object?.notify ?? true;
  object.config.headers = {
    Authorization: `JWT ${token}`,
    devicetype: object.deviceType ?? 1,
    'Cache-Control': 'no-cache',
    'Content-Type': object.isMultipart ? undefined : 'application/json',
    ...object?.config?.headers,
  };

  if (object.isMultipart) {
    object.config.transformRequest = {};
    object.config.onUploadProgress = myUploadProgress;
  }

  if (object.showLoader) LoaderService.startLoading();
  const { method, url, data, config, notify } = object;

  try {
    const response = await axios.request({ url, method, data, ...config });
    if (object.showLoader) LoaderService.stopLoading();

    if (response?.data?.code === 'E_UNAUTHORIZED') catchError(commonLogout);

    void catchError(() => callback?.(response.data?.code !== 'OK', response.data));
    return [response.data?.code !== 'OK', response.data];
  } catch (error) {
    logWarn({ url, method, data, config, error });
    if (object.showLoader) LoaderService.stopLoading();

    if (notify) Notify.error({ message: error.response?.data?.message ?? 'Something bad happened' });

    void catchError(() => callback?.(error || true, error.response ?? error));
    return [error || true, error.response ?? error];
  }
};

const UtilService = {
  callApi,
  getFullName: getUserName,
  getLocalStorageItem: (key) => Storage.get(key),
  setLocalStorageItem: (value, key) => Storage.set(key, value),
  subString: (input, ...args) => input?.substring?.(...args),
  fileMimeType: (blob, pdf, cb) => {
    const type = blob.type;

    const fileReader = new FileReader();
    fileReader.onloadend = function (e) {
      const arr = new Uint8Array(e.target.result).subarray(0, 4);
      let header = '';
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }
      let validity = false;
      if (pdf) {
        if (type in FILE_TYPES_PDF) {
          validity = FILE_TYPES_PDF[type].includes(header.toLowerCase());

          cb(validity);
        } else {
          cb(false);
        }
      } else {
        if (type in FILE_TYPES) {
          validity = FILE_TYPES[type].includes(header.toLowerCase());
          cb(validity);
        } else {
          cb(false);
        }
      }
    };
    fileReader.readAsArrayBuffer(blob);
  },
};

export const getTrackData = (str) => {
  const [page, section, action] = str.split('/');
  return { page, section, action };
};

export const track = (data) =>
  UtilService.getLocalStorageItem(`${LOCAL_STORAGE_VAR}-token`)?.length &&
  UtilService.callApi({ ...CREATE_ANALYTICS, request: getTrackData(data) });

window.track = track;

export const getShortPath = () => window.location.pathname.split('/')[1];
window.shortPath = getShortPath;

export const loaderActivityFn = (showLoader) => {
  const getLoaderEle = document.querySelectorAll('#loader.APILoader')[0];
  const getProgressBar = document.querySelectorAll('#loader.APILoader .showProgress')[0];
  if (showLoader) {
    getLoaderEle.classList.add('loading');
  } else if (getLoaderEle && getLoaderEle.classList && getLoaderEle.classList.contains('loading')) {
    getLoaderEle.classList.remove('loading');
    if (getProgressBar && getProgressBar.classList && !getProgressBar.classList.contains('display-none')) {
      getProgressBar.classList.add('display-none');
    }
  }
};

export const getCurrentTime = () => {
  return new Promise((resolve, reject) => {
    const objData = { ...GET_TIME };
    UtilService.callApi(objData, (err, res) => {
      if (err) return reject(err);
      if (res?.code === 'OK') return resolve(res.data);
      reject(err);
    });
  });
};

export const getNextBidData = (type = null) => {
  return new Promise((resolve, reject) => {
    const objData = { ...NEXT_BID };
    objData.url = `${objData.url}${objectToQueryString({
      type: type ? type : 1,
    })}`;

    UtilService.callApi(objData, (err, data) => {
      if (data && data.code === 'OK') return resolve(data.data);
      reject(err);
    });
  });
};

export default UtilService;
